import { useLocation } from "@tanstack/react-router";
import { useLayoutEffect } from "react";

// Where sign-in lands: `/` redirects to the playground chat. public/theme-init.js
// repeats it for the first paint; app-shell-skeleton.test.ts keeps them equal.
export const CHAT_LANDING_PATH =
  /^\/(orgs\/[^/]+\/projects\/[^/]+\/playground\/chat(\/.*)?)?$/;

export const isChatLandingPath = (pathname: string) =>
  CHAT_LANDING_PATH.test(pathname);

// Set once the user has reached the dashboard layout, cleared on sign-out, on a
// redirect to login, and when `/` sends them to onboarding. Without it the
// loading UI stays blank instead of drawing a frame the visitor is not about to
// see. Cosmetic only: auth is enforced by the API. public/theme-init.js reads it
// for the first paint.
export const SHELL_HINT_KEY = "arcade-dashboard-shell";

export const setShellHint = (on: boolean) => {
  try {
    if (on) {
      localStorage.setItem(SHELL_HINT_KEY, "1");
    } else {
      localStorage.removeItem(SHELL_HINT_KEY);
    }
  } catch {
    // Storage can be blocked by browser policy; the loading UI stays blank.
  }
};

const hasShellHint = () => {
  try {
    return localStorage.getItem(SHELL_HINT_KEY) === "1";
  } catch {
    return false;
  }
};

/**
 * Keeps the loader up while any AppShellSkeleton shows it. The loader starts
 * visible; the last skeleton to go hides it, and `release` hides it after the
 * first page loads when none ever showed it, as on the login page.
 */
export const createLoaderVisibility = (
  setVisible: (visible: boolean) => void
) => {
  let shownBy = 0;
  return {
    show: () => {
      shownBy += 1;
      setVisible(true);
    },
    hide: () => {
      shownBy -= 1;
      if (shownBy === 0) {
        setVisible(false);
      }
    },
    release: () => {
      if (shownBy === 0) {
        setVisible(false);
      }
    },
  };
};

const loader = createLoaderVisibility((visible) => {
  const element = document.getElementById("initial-loader");
  if (element) {
    element.hidden = !visible;
  }
});

/** Called once the first page has loaded. */
export const releaseInitialLoader = loader.release;

/**
 * The dashboard frame while the session, org/project context and capabilities
 * resolve. It shows the loader in index.html, the same element the browser
 * paints before the app's code arrives, so the frame looks identical before
 * and after React takes over. The content area previews the playground chat
 * only when that is the destination; any other page shows its own skeleton
 * once it renders.
 */
export const AppShellSkeleton = () => {
  const isChatLanding = useLocation({
    select: ({ pathname }) => isChatLandingPath(pathname),
  });
  useLayoutEffect(() => {
    const classes = document.documentElement.classList;
    classes.toggle("il-plain", !isChatLanding);
    classes.toggle("il-blank", !hasShellHint());
    loader.show();
    return loader.hide;
  }, [isChatLanding]);
  return null;
};
